import React, { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHomepageSettings } from '../hooks/useHomepage';

const Homepage: React.FC = memo(() => {
  const navigate = useNavigate();
  const { data: homepageSettings, isLoading, error } = useHomepageSettings();
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Reset image loaded state when homepage settings change
    setImageLoaded(false);
  }, [homepageSettings]);

  // Check if both click name and URL are provided to enable clicking
  const heroClickEnabled = Boolean(
    homepageSettings?.hero_click_name &&
    homepageSettings?.hero_click_url
  );

  const handleImageClick = () => {
    if (!heroClickEnabled) return;

    const clickUrl = homepageSettings?.hero_click_url;
    if (!clickUrl) return;

    // Check if it's an internal navigation or external link
    if (clickUrl.startsWith('/')) {
      // Internal navigation using React Router
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(clickUrl);
    } else {
      // External link - navigate in same tab
      window.location.href = clickUrl;
    }
  };

  // Default fallback values (from current Figma design)
  const defaultImageUrl = "https://usnhxnxfrynwiplufpcv.supabase.co/storage/v1/object/public/general_settings/images/homepage_image_fit.jpg";
  const defaultMainText = `במרחב פיזי מיוחד, אנחנו מציעים סביבת לימוד ויצירה ייחודית המאפשרת לכל סטודנט לפתח את הקול האמנותי שלו. הסטודיו ממוקם בבניין היסטורי בלב תל אביב, המהווה מקור השראה בפני עצמו לתהליך היצירתי של הסטודנטים.

המרחב נועד לעודד דיאלוג פתוח בין תחומים שונים ולאפשר חקירה עמוקה של רעיונות אמנותיים מגוונים. כל פרויקט הוא הזדמנות לגלות נקודות מבט חדשות ולהעמיק בהבנה של התרבות הישראלית העכשווית.`;
  const defaultClickName = 'מחזור ז\' – לתערוכה';

  // Image loading strategy: Show local image first, then database image if available
  const databaseImageUrl = homepageSettings?.image_url;
  const shouldShowDatabaseImage = databaseImageUrl && imageLoaded && !isLoading;
  const heroImageUrl = shouldShowDatabaseImage ? databaseImageUrl : defaultImageUrl;
  const mainText = homepageSettings?.main_text || defaultMainText;
  const heroClickName = homepageSettings?.hero_click_name || defaultClickName;
  
  // Preload database image when API response is available
  useEffect(() => {
    if (databaseImageUrl && !imageLoaded) {
      const img = new Image();
      img.onload = () => {
        setImageLoaded(true);
      };
      img.onerror = () => {
        // Failed to preload database image, staying with local image
      };
      img.src = databaseImageUrl;
    }
  }, [databaseImageUrl, imageLoaded]);

  return (
    <div className="view active">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-image hero-image-container">
          <img 
            src={heroImageUrl} 
            alt="בניין סטודיו אנקורי" 
            id="heroImage"
            loading="eager" // Load immediately since it's above the fold
            onError={(e) => {
              // Fallback image if main image fails to load
              const target = e.target as HTMLImageElement;
              // Try the default image first, then fallback to external
              if (target.src !== defaultImageUrl) {
                target.src = defaultImageUrl;
              } else {
                target.src = 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=600&q=80';
              }
            }}
          />
          {/* Hover overlay - only show if click is enabled */}
          {heroClickEnabled && (
            <div className="hero-image-overlay" onClick={handleImageClick}>
              <div className="hero-overlay-content">
                <span className="hero-overlay-text">{heroClickName}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Content Section */}
      <section className="content">
        <div className="content-container">
          <div className="content-columns">
            {/* Right Column - Dynamic content from database (first 2 paragraphs) */}
            <div className="content-column content-right">
              {isLoading ? (
                <div style={{
                  background: '#f0f0f0',
                  height: '100px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                  fontSize: '14px'
                }}>
                  טוען תוכן...
                </div>
              ) : (
                <>
                  {mainText.split('\n\n').slice(0, 2).map((paragraph: string, index: number) => (
                    <p key={index} className="content-text">
                      {paragraph}
                    </p>
                  ))}
                </>
              )}
            </div>

            {/* Left Column - Third and fourth paragraphs from database */}
            <div className="content-column content-left">
              {!isLoading && (
                <>
                  {mainText.split('\n\n')[2] && (
                    <p className="content-text">
                      {mainText.split('\n\n')[2]}
                    </p>
                  )}
                  {mainText.split('\n\n')[3] && (
                    <p className="content-text">
                      {mainText.split('\n\n')[3]}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
});

Homepage.displayName = 'Homepage';

export default Homepage;